/** Shared canvas attribute assembly; FN-2b plans against these exact rules. */
import {
  hasImagePositionOverride,
  hasImageHoverOverride,
  isValidImageHoverPreset,
  hasImageEffectOverride,
  isValidImageEffect
} from '../editing/image-runtime-css.js';

export function applyPreviewElementAttributeOverrides(html, section) {
  const elements = section?.overrides?.elements;
  if (!elements && !String(html || '').includes('data-image-')) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;

  let changed = false;
  const clearImageAttrs = (img) => {
    for (const attr of [
      'data-image-edit',
      'data-image-hover',
      'data-image-effect',
      'data-image-dragging'
    ]) {
      if (img.hasAttribute(attr)) {
        img.removeAttribute(attr);
        changed = true;
      }
    }
  };
  if (
    root.matches?.(
      'img[data-image-edit], img[data-image-hover], img[data-image-effect], img[data-image-dragging]'
    )
  ) {
    clearImageAttrs(root);
  }
  for (const img of root.querySelectorAll(
    'img[data-image-edit], img[data-image-hover], img[data-image-effect], img[data-image-dragging]'
  )) {
    clearImageAttrs(img);
  }

  if (!elements) return changed ? root.innerHTML : html;

  for (const [eid, props] of Object.entries(elements)) {
    if (!eid || !props) continue;

    const el = Array.from(root.querySelectorAll('[data-pb-eid]')).find(
      (node) => node.getAttribute('data-pb-eid') === eid
    );
    if (!el) continue;

    // SVG preserveAspectRatio
    if (
      Object.prototype.hasOwnProperty.call(props, 'preserveAspectRatio') &&
      el.tagName.toLowerCase() === 'svg'
    ) {
      if (props.preserveAspectRatio) {
        el.setAttribute('preserveAspectRatio', props.preserveAspectRatio);
      } else {
        el.removeAttribute('preserveAspectRatio');
      }
      changed = true;
    }

    // Image position + hover preset → engaged attributes on <img>. The
    // runtime CSS rule that composes the transform is gated by these
    // attrs, so images without overrides stay untouched.
    if (el.tagName.toLowerCase() === 'img') {
      const wantsEdit =
        hasImagePositionOverride(props) ||
        hasImageHoverOverride(props) ||
        hasImageEffectOverride(props);
      if (wantsEdit) {
        if (!el.hasAttribute('data-image-edit')) {
          el.setAttribute('data-image-edit', '');
          changed = true;
        }
      } else if (el.hasAttribute('data-image-edit')) {
        el.removeAttribute('data-image-edit');
        changed = true;
      }

      if (hasImageHoverOverride(props) && isValidImageHoverPreset(props.imageHover)) {
        if (el.getAttribute('data-image-hover') !== props.imageHover) {
          el.setAttribute('data-image-hover', props.imageHover);
          changed = true;
        }
      } else if (el.hasAttribute('data-image-hover')) {
        el.removeAttribute('data-image-hover');
        changed = true;
      }

      if (hasImageEffectOverride(props) && isValidImageEffect(props.imageEffect)) {
        if (el.getAttribute('data-image-effect') !== props.imageEffect) {
          el.setAttribute('data-image-effect', props.imageEffect);
          changed = true;
        }
      } else if (el.hasAttribute('data-image-effect')) {
        el.removeAttribute('data-image-effect');
        changed = true;
      }
    }
  }

  return changed ? root.innerHTML : html;
}
